<template>
  <div id="sac-input-container">
    <div class="input-inner-container">
      <div v-if="prefix">
        <span class="sac__font-14 n60 ml-4">{{ prefix }}</span>
      </div>
      <textarea
        v-if="type === 'textarea'"
        :id="id || field"
        :class="[
          label ? 'label' : '',
          'sac-textarea',
          disable ? 'disable' : '',
        ]"
        :type="type || 'text'"
        :value="value"
        :placeholder="placeholder"
        :min="min || null"
        :maxlength="max || null"
        :disabled="disable"
        @input="change(field, $event.target.value)"
      />
      <vue-google-autocomplete
        v-else-if="field === 'address1' && !disable"
        :id="id || 'shipping-gac'"
        classname="i-field"
        :placeholder="placeholder"
        @placechanged="handlePlaceChange"
        @inputChange="handleChange"
      />
      <input
        v-else-if="isV2"
        :id="id || field"
        :class="[error && !showError ? 'sac-input input-error' : 'sac-input']"
        :type="
          type !== 'number' && type === 'password' && !showPassword
            ? type
            : undefined
        "
        :value="value"
        :placeholder="placeholder"
        :min="min || null"
        :readOnly="disable"
        @input="change(field, $event.target.value)"
        @keypress="isNumber"
      />
      <input
        v-else
        :id="id || field"
        :class="[label ? 'label' : '', 'sac-input', disable ? 'disable' : '']"
        :type="
          type !== 'number' && type === 'password' && !showPassword
            ? type
            : undefined
        "
        :value="value"
        :placeholder="placeholder"
        :min="min || null"
        :maxlength="max || null"
        :readOnly="disable"
        @input="change(field, $event.target.value)"
        @keypress="isNumber"
      />
    </div>
    <label
      v-if="label"
      :class="[value && value.length > 0 ? 'active' : '', 'sac-input-label']"
      :for="field"
    >
      {{ label }}
    </label>
    <span v-if="error && !showError" class="error-text">
      {{ error }}
    </span>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VueGoogleAutocomplete from 'vue-google-autocomplete';

export default {
  name: 'SACInput',
  components: {
    VueGoogleAutocomplete,
  },
  props: {
    id: String,
    label: String,
    field: String,
    value: [String, Number],
    placeholder: String,
    change: Function,
    type: String,
    restrict: String,
    min: String,
    prefix: String,
    max: Number,
    error: String,
    disable: {
      type: Boolean,
      default: false,
    },
    isV2: {
      type: Boolean,
      default: false,
    },
    showError: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      countries: (state) => state.general.countries,
    }),
    elementId() {
      return this.id || 'shipping-gac';
    },
  },
  watch: {
    value(v) {
      if (this.field === 'address1') {
        const autoComplete = document.getElementById(this.elementId);
        if (autoComplete) {
          autoComplete.value = v;
        }
      }
    },
  },
  mounted() {
    const autoComplete = document.getElementById(this.elementId);
    if (autoComplete && this.field === 'address1' && this.value) {
      autoComplete.value = this.value;
    }
  },
  methods: {
    handleChange(v) {
      const { newVal } = v;
      this.change(this.field, newVal);
    },
    handlePlaceChange(place) {
      const {
        street_number: streetNum,
        route,
        locality: city,
        administrative_area_level_1: state,
        postal_code: zip,
        latitude,
        longitude,
        country,
      } = place;
      const address1 = streetNum ? `${streetNum} ${route}` : route;
      const newShippingAddress = {
        address1,
        city,
        state,
        zip,
        longitude,
        latitude,
      };
      if (country) {
        const c = this.countries.find((count) => count.country === country);
        if (c) {
          newShippingAddress.country = c.code;
        }
      }
      document.getElementById(this.elementId).value = address1;
      this.change('address', newShippingAddress);
    },
    isNumber(evt) {
      if (this.type === 'number') {
        const event = evt || window.event;
        if (
          this.restrict === 'phone-number' &&
          event.target.value.length > 11
        ) {
          event.preventDefault();
        }
        if (this.restrict === 'zip' && event.target.value.length >= 5) {
          event.preventDefault();
        }
        const charCode = event.which ? event.which : event.keyCode;
        if (
          charCode > 31 &&
          (charCode < 48 || charCode > 57) &&
          charCode !== 46
        ) {
          event.preventDefault();
        } else {
          return true;
        }
      }
      return true;
    },
  },
};
</script>

<style lang="scss">
#sac-input-container {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 2px;
  .input-inner-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    background-color: $NBLUE5;
    width: 100%;
  }
  input {
    width: 100%;
    padding-left: 10px;
    padding-right: 10px;
    background-color: $NBLUE5;
    height: 44px;
    &.label {
      padding-top: 12px;
    }
    &:focus + .sac-input-label {
      color: $N100;
      transform: scale(0.6) translate(-20px, -30px);
    }
    &.disable {
      background-color: $NBLUE5;
      opacity: 0.6;
      pointer-events: none;
    }
  }
  .input-error {
    padding-left: 10px;
    padding-right: 10px;
    width: 100%;
    background-color: #fbd6d4;
    height: 48px;
    &.label {
      padding-top: 12px;
    }
    &:focus + .sac-input-label {
      color: $N100;
      transform: scale(0.6) translate(-20px, -30px);
    }
  }
  textarea {
    padding-left: 10px;
    padding-right: 10px;
    width: 100%;
    background-color: $NBLUE5;
    height: 48px;
    padding-top: 12px;
    &.disable {
      background-color: $N10;
      opacity: 0.5;
    }
  }
  .sac-textarea {
    padding-left: 10px;
    padding-right: 10px;
    width: 100%;
    height: 240px;
    &.label {
      padding-top: 12px;
    }
    &:focus + .sac-input-label {
      color: $N100;
      transform: scale(0.6) translate(-20px, -30px);
    }
  }
  .sac-input-label {
    position: absolute;
    left: 8px;
    top: 14px;
    color: $N30;
    transition: all 0.2s ease;
    &.active {
      color: $N100;
      transform: scale(0.6) translate(-20px, -30px);
    }
  }
  .error-text {
    color: $TOMATO100 !important;
    font-size: 0.875rem;
    font-weight: 500;
    padding-left: 4px;
    margin-top: 5px;
  }
}
</style>
